import type { ReduxLoggerConfig } from './reduxMiddleware';

/**
 * Configuration for Redux logging in different environments
 */
export const reduxLoggerConfigs = {
  development: {
    skipActions: [
      // High-frequency UI state changes
      'core/theme/SET_THEME_LOADING',
      'core/settingsDialog/SET_OPEN',
      // Redux persist actions
      'persist/FLUSH',
      'persist/REHYDRATE',
      'persist/PAUSE',
      'persist/PERSIST',
      'persist/PURGE',
      'persist/REGISTER',
    ],
    forceLogActions: [
      // Authentication actions
      'auth/',
      // API-related actions
      '/pending',
      '/fulfilled',
      '/rejected',
      // User interaction actions
      'communities/',
      'safetyForge/',
    ],
    loggedStatePaths: ['auth.isAuthenticated', 'auth.user.id', 'auth.currentProviderId', 'safetyForge.classification.isLoading'],
    slowActionThreshold: 50, // More sensitive in development
    logPayloads: true,
    logStateChanges: true,
  } as ReduxLoggerConfig,

  production: {
    skipActions: [
      // High-frequency UI state changes
      'core/theme/SET_THEME_LOADING',
      'core/settingsDialog/SET_OPEN',
      // Redux persist actions
      'persist/FLUSH',
      'persist/REHYDRATE',
      'persist/PAUSE',
      'persist/PERSIST',
      'persist/PURGE',
      'persist/REGISTER',
      // Additional production skips
      'core/theme/',
      'ui/',
    ],
    forceLogActions: [
      // Only critical actions in production
      'auth/login',
      'auth/logout',
      'auth/refreshToken',
      '/rejected', // Always log errors
    ],
    loggedStatePaths: ['auth.isAuthenticated', 'auth.user.id'],
    slowActionThreshold: 200, // Less sensitive in production
    logPayloads: false, // Don't log payloads in production for privacy
    logStateChanges: false, // Don't log state changes in production
  } as ReduxLoggerConfig,

  test: {
    skipActions: [
      // Skip most actions in tests
      'persist/',
      'core/theme/',
    ],
    forceLogActions: [],
    loggedStatePaths: [],
    slowActionThreshold: 1000, // Very high threshold for tests
    logPayloads: false,
    logStateChanges: false,
  } as ReduxLoggerConfig,
};

/**
 * Sensitive field patterns for data redaction
 */
export const sensitiveFieldPatterns = [
  'password',
  'token',
  'secret',
  'authorization',
  'accessToken',
  'refreshToken',
  'apiKey',
  'clientSecret',
  'privateKey',
  'sessionId',
  'csrf',
  'nonce',
];

/**
 * Action type patterns that should always be logged (security-related)
 */
export const securityActionPatterns = [
  'auth/login',
  'auth/logout',
  'auth/refreshToken',
  'auth/sessionExpired',
  'auth/unauthorized',
  'permission/',
  'security/',
];

/**
 * Action type patterns that should never be logged (high-frequency/noise)
 */
export const noiseActionPatterns = ['ui/hover', 'ui/focus', 'ui/blur', 'ui/scroll', 'ui/resize', 'theme/loading', 'animation/', 'tooltip/'];

/**
 * Performance thresholds for different action categories
 */
export const performanceThresholds = {
  api: 1000, // API calls should complete within 1s
  auth: 2000, // Auth operations can take up to 2s
  ui: 100, // UI updates should be fast
  data: 500, // Data processing should be reasonable
  default: 200, // Default threshold for other actions
};

/**
 * Get current environment, handling Jest compatibility
 */
function getEnvironment(): string {
  // In Jest/Node environment, use process.env
  if (typeof process !== 'undefined' && process.env?.NODE_ENV) {
    return process.env.NODE_ENV;
  }

  // Use import.meta.env directly - no eval needed
  return import.meta.env.MODE || 'development';
}

/**
 * Get Redux logger configuration for current environment
 */
export function getReduxLoggerConfig(): ReduxLoggerConfig {
  // Handle Jest environment where import.meta.env is not available
  const environment = getEnvironment();

  switch (environment) {
    case 'production':
      return reduxLoggerConfigs.production;
    case 'test':
      return reduxLoggerConfigs.test;
    case 'development':
    default:
      return reduxLoggerConfigs.development;
  }
}

/**
 * Check if an action type matches any of the given patterns
 */
export function matchesActionPattern(actionType: string, patterns: string[]): boolean {
  return patterns.some(pattern => actionType.includes(pattern));
}

/**
 * Get performance threshold for an action type
 */
export function getPerformanceThreshold(actionType: string): number {
  if (matchesActionPattern(actionType, ['api/', '/api', 'fetch', 'load'])) {
    return performanceThresholds.api;
  }

  if (matchesActionPattern(actionType, ['auth/'])) {
    return performanceThresholds.auth;
  }

  if (matchesActionPattern(actionType, ['ui/', 'theme/', 'dialog/'])) {
    return performanceThresholds.ui;
  }

  if (matchesActionPattern(actionType, ['process', 'calculate', 'transform'])) {
    return performanceThresholds.data;
  }

  return performanceThresholds.default;
}

/**
 * Check if an action should be treated as security-sensitive
 */
export function isSecurityAction(actionType: string): boolean {
  return matchesActionPattern(actionType, securityActionPatterns);
}

/**
 * Check if an action should be considered noise and potentially skipped
 */
export function isNoiseAction(actionType: string): boolean {
  return matchesActionPattern(actionType, noiseActionPatterns);
}
